// src/pages/Users.js
import React, { useEffect, useMemo, useState } from "react";
import { collection, deleteDoc, doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { FaTrash, FaExclamationCircle, FaCheckCircle } from "react-icons/fa";
import { FiSearch, FiFilter, FiUser, FiPhone, FiMapPin, FiAtSign, FiCalendar, FiX } from "react-icons/fi";

/* Theme */
const COLORS = {
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  card: "#ffffff",
  primary: "#f20c53",
  secondary: "#fb6c33",
  page: "#f5f6f8",
  ok: "#16a34a",
  warn: "#b91c1c",
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [canDelete, setCanDelete] = useState(false);
  const [canView, setCanView] = useState(true);
  const [loading, setLoading] = useState(true);

  // UI
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("All");
  const [toast, setToast] = useState(null); // {type: 'success'|'error', message: string}
  const [deletingId, setDeletingId] = useState("");

  useEffect(() => {
    try {
      const employee = JSON.parse(localStorage.getItem("employee"));
      const role = employee?.role;
      const permissions = employee?.permissions || [];

      if (role === "super_admin") {
        setCanDelete(true);
        setCanView(true);
      } else {
        setCanDelete(permissions.includes("delete_user"));
        setCanView(permissions.includes("view_users"));
      }
    } catch {
      setCanView(false);
    }
  }, []);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    const unsubscribe = onSnapshot(collection(db, "users"), (snapshot) => {
      const usersData = snapshot.docs.map((d) => {
        const x = d.data() || {};
        return {
          id: d.id,
          uid: x.uid || d.id || "-",
          name: x.name || "-",
          phone: x.phone || x.mobile || "-",
          username: x.username || "-",
          dob: x.dob || "",
          city: x.city || "-",
        };
      });
      setUsers(sortByName(usersData));
      setLoading(false);
    });
    return () => unsubscribe();
  }, [canView]);

  const cities = useMemo(() => {
    const set = new Set(users.map((u) => (u.city || "-").trim()).filter(Boolean));
    return ["All", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [users]);

  const filtered = useMemo(() => {
    let arr = [...users];
    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter((u) => {
        const hay = `${u.uid} ${u.name} ${u.username} ${u.phone} ${u.city}`.toLowerCase();
        return hay.includes(q);
      });
    }
    if (cityFilter !== "All") {
      arr = arr.filter((u) => String(u.city || "-") === cityFilter);
    }
    return arr;
  }, [users, search, cityFilter]);

  function showToast(message, type = "success") {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2200);
  }

  const handleDelete = async (id) => {
    if (!canDelete) {
      showToast("❌ You do not have permission to delete users.", "error");
      return;
    }
    try {
      setDeletingId(id);
      await deleteDoc(doc(db, "users", id));
      setConfirmDelete(null);
      showToast("🗑️ User deleted", "success");
    } catch (err) {
      console.error("Delete error:", err);
      showToast("❌ Failed to delete user.", "error");
    } finally {
      setDeletingId("");
    }
  };

  if (!canView) {
    return (
      <div style={deniedOverlay}>
        <div style={deniedBox}>
          <FaExclamationCircle size={32} color="#e53935" />
          <h3 style={{ marginTop: "1rem" }}>Access Denied</h3>
          <p style={{ color: "#666" }}>You do not have permission to view user data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="aa-pro-page" style={{ padding: 16, fontFamily: "Inter, system-ui, sans-serif", background: COLORS.page, minHeight: "100vh" }}>
      {/* Hero */}
      <div className="aa-pro-hero" style={hero}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="aa-pro-hero-icon" style={heroIcon}>
            <FiUser />
          </div>
          <div>
            <h2 className="aa-pro-title" style={heroTitle}>Users</h2>
            <p className="aa-pro-subtitle" style={heroSub}>Search, filter, and manage registered users.</p>
          </div>
        </div>
        <div className="aa-pro-metrics" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px,1fr))", gap: 8, minWidth: 280 }}>
          <Stat label="Total" value={users.length} />
          <Stat label="Cities" value={Math.max(0, cities.length - 1)} />
        </div>
      </div>

      {/* Controls */}
      <div className="aa-pro-toolbar" style={controlsRow}>
        <div style={searchBox}>
          <FiSearch style={{ opacity: 0.7 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone, username, id, city…"
            style={searchInput}
          />
        </div>
        <div style={controlGroup}>
          <FiFilter />
          <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} style={selectBare}>
            {cities.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="aa-pro-table-shell" style={{ overflowX: "auto", background: COLORS.card, borderRadius: 12, border: `1px solid ${COLORS.border}`, boxShadow: "0 6px 18px rgba(0,0,0,.06)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 980 }}>
          <thead>
            <tr>
              <Th sticky>UID</Th>
              <Th sticky>Name</Th>
              <Th sticky>Phone</Th>
              <Th sticky>Username</Th>
              <Th sticky>DOB</Th>
              <Th sticky>City</Th>
              <Th sticky>Action</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={tdMuted}>
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} style={tdMuted}>
                  No users found.
                </td>
              </tr>
            ) : (
              filtered.map((u, i) => {
                const rowBg = i % 2 ? "#fafafa" : "#fff";
                return (
                  <tr
                    key={u.id}
                    style={{
                      borderTop: `1px solid ${COLORS.border}`,
                      background: rowBg,
                      transition: "background .15s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = rowBg)}
                  >
                    <Td mono title={u.uid}>
                      {shorten(u.uid, 10)}
                    </Td>
                    <Td>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <FiUser /> {u.name || "—"}
                      </div>
                    </Td>
                    <Td>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <FiPhone /> {u.phone || "—"}
                      </div>
                    </Td>
                    <Td>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <FiAtSign /> {u.username || "—"}
                      </div>
                    </Td>
                    <Td>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <FiCalendar /> {formatDateOnly(u.dob) || "—"}
                      </div>
                    </Td>
                    <Td>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <FiMapPin /> {u.city || "—"}
                      </div>
                    </Td>
                    <Td>
                      {canDelete ? (
                        <button
                          onClick={() => setConfirmDelete(u)}
                          style={iconDangerBtn}
                          title="Delete user"
                        >
                          <FaTrash />
                          Delete
                        </button>
                      ) : (
                        <span style={{ color: COLORS.subtext, fontSize: 12 }}>No permission</span>
                      )}
                    </Td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Confirm Delete Modal */}
      {confirmDelete && (
        <div style={modalBackdrop} onClick={() => setConfirmDelete(null)} role="dialog" aria-modal="true">
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontWeight: 900, color: COLORS.text }}>Confirm Deletion</div>
              <button onClick={() => setConfirmDelete(null)} style={iconBtn} aria-label="Close">
                <FiX />
              </button>
            </div>
            <div style={{ color: COLORS.text }}>
              Are you sure you want to delete <b>{confirmDelete.name || confirmDelete.uid}</b>?
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
              <button
                onClick={() => setConfirmDelete(null)}
                style={ghostBtn}
                disabled={!!deletingId}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(confirmDelete.id)}
                style={{ ...primaryBtn, background: `linear-gradient(90deg, ${COLORS.warn}, #ef4444)` }}
                disabled={!!deletingId}
                title="Delete user"
              >
                {deletingId ? "Deleting…" : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 16,
            right: 16,
            background: toast.type === "error" ? "#fef2f2" : "#ecfdf5",
            border: `1px solid ${toast.type === "error" ? "#fecaca" : "#86efac"}`,
            borderRadius: 10,
            padding: "10px 12px",
            boxShadow: "0 8px 24px rgba(0,0,0,.12)",
            color: COLORS.text,
            fontWeight: 800,
            zIndex: 90,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {toast.type === "error" ? <FaExclamationCircle /> : <FaCheckCircle />}
          {toast.message}
        </div>
      )}
    </div>
  );
}

/* ---------- Small parts ---------- */
function Th({ children, sticky }) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: 10,
        fontWeight: 800,
        color: COLORS.text,
        background: "#f8fafc",
        position: sticky ? "sticky" : "static",
        top: 0,
        zIndex: 5,
        borderBottom: `1px solid ${COLORS.border}`,
      }}
    >
      {children}
    </th>
  );
}
function Td({ children, mono, title }) {
  return (
    <td
      title={title}
      style={{
        padding: 10,
        color: COLORS.text,
        verticalAlign: "top",
        fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, monospace" : "inherit",
      }}
    >
      {children}
    </td>
  );
}

/* ---------- Helpers ---------- */
function sortByName(arr) {
  return [...arr].sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
}
function shorten(s, n = 10) {
  if (!s) return "—";
  return s.length > n ? `${s.slice(0, n)}…` : s;
}
function formatDateOnly(v) {
  try {
    if (!v) return "";
    // supports ISO string, Date, timestamp number, or plain string; leaves plain string as-is if unparseable
    if (typeof v === "string") {
      const d = new Date(v);
      return isNaN(d.getTime()) ? v : d.toLocaleDateString();
    }
    const d = v?.toDate ? v.toDate() : new Date(v);
    return isNaN(d.getTime()) ? "" : d.toLocaleDateString();
  } catch {
    return "";
  }
}

/* ---------- Styles ---------- */
const hero = {
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  color: "#fff",
  padding: "14px 16px",
  borderRadius: 14,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  boxShadow: "0 14px 26px rgba(0,0,0,0.12)",
  marginBottom: 12,
};
const heroIcon = {
  width: 42,
  height: 42,
  borderRadius: 10,
  display: "grid",
  placeItems: "center",
  background: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.35)",
};
const heroTitle = { margin: 0, fontSize: 20, fontWeight: 900, lineHeight: 1.1 };
const heroSub = { margin: "4px 0 0", opacity: 0.92, fontSize: 13 };

function Stat({ label, value }) {
  return (
    <div
      className="aa-pro-metric"
      style={{
        background: "#fff",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 10,
        padding: "10px 12px",
        minWidth: 120,
        display: "grid",
        gap: 2,
      }}
    >
      <div style={{ fontSize: 12, color: COLORS.subtext }}>{label}</div>
      <div style={{ fontWeight: 900, color: COLORS.text, fontSize: 18 }}>{value}</div>
    </div>
  );
}

const controlsRow = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
  marginBottom: 12,
};
const searchBox = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "10px 12px",
  background: "#fff",
  minWidth: 260,
  flex: 1,
};
const searchInput = { border: "none", outline: "none", flex: 1, fontSize: 14 };
const controlGroup = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "8px 10px",
  background: "#fff",
};
const selectBare = {
  border: "none",
  outline: "none",
  background: "transparent",
  fontSize: 14,
  paddingRight: 4,
  cursor: "pointer",
};

const tdMuted = { padding: 12, color: COLORS.subtext };

const iconBtn = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  width: 34,
  height: 34,
  borderRadius: 8,
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
};

const iconDangerBtn = {
  border: `1px solid #fecaca`,
  background: "#fff",
  color: COLORS.warn,
  height: 34,
  padding: "0 12px",
  borderRadius: 8,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  cursor: "pointer",
  fontWeight: 800,
};

const modalBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.45)",
  display: "grid",
  placeItems: "center",
  zIndex: 60,
  padding: 16,
};
const modalCard = {
  width: "100%",
  maxWidth: 520,
  background: "#fff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 14,
  padding: 14,
  boxShadow: "0 10px 30px rgba(0,0,0,.25)",
};

/* ✅ Missing buttons added */
const ghostBtn = {
  height: 34,
  padding: "0 12px",
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  cursor: "pointer",
  fontWeight: 800,
};
const primaryBtn = {
  height: 34,
  padding: "0 14px",
  borderRadius: 8,
  border: "none",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const deniedOverlay = {
  backdropFilter: "blur(6px)",
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  width: "100vw",
  height: "100vh",
  position: "fixed",
  top: 0,
  left: 0,
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const deniedBox = {
  background: "#fff",
  padding: "2rem",
  borderRadius: "12px",
  boxShadow: "0 6px 18px rgba(0,0,0,0.3)",
  textAlign: "center",
  maxWidth: "420px",
};

/* End */
